import React, { useEffect } from 'react';
import { useDemo } from '../context/DemoContext';
import { modelRegistryAPI } from '../api';
import { useActiveModel } from '../context/ActiveModelContext';
import './DemoRunner.css';

function DemoRunnerContent() {
  const {
    isDemoRunning,
    demoStep,
    demoModel,
    demoError,
    isDemoComplete,
    demoConfig,
    startDemo,
    resetDemo
  } = useDemo();

  const { setActiveModel } = useActiveModel();

  // Update active model when demo model is ready (fetch full metadata if available)
  useEffect(() => {
    const syncActiveModel = async () => {
      if (!demoModel) return;
      const trained = demoModel || {};
      const modelId = trained.model_registry_id || trained.model_id || trained.id;
      if (!modelId) return;
      try {
        const full = await modelRegistryAPI.getModelInfo(modelId).catch(() => null);
        const merged = full ? {
          ...full,
          id: full.id || modelId,
          dataset_name: full.dataset_name || trained.dataset_name || demoConfig.dataset,
          model_type: full.model_type || trained.model_type || demoConfig.modelType,
          metrics: full.metrics || trained.metrics || {}
        } : {
          id: modelId,
          dataset_name: trained.dataset_name || demoConfig.dataset,
          model_type: trained.model_type || demoConfig.modelType,
          trained_date: new Date().toISOString(),
          metrics: trained.metrics || {}
        };
        setActiveModel(merged);
        window.dispatchEvent(new CustomEvent('demo:activeModelSet', { detail: { modelId: merged.id } }));
      } catch (e) {
        setActiveModel({
          id: modelId,
          dataset_name: trained.dataset_name || demoConfig.dataset,
          model_type: trained.model_type || demoConfig.modelType,
          trained_date: new Date().toISOString(),
          metrics: trained.metrics || {}
        });
        window.dispatchEvent(new CustomEvent('demo:activeModelSet', { detail: { modelId } }));
      }
    };
    syncActiveModel();
  }, [demoModel, demoConfig, setActiveModel]);

  // Let the dashboard collapse this panel once the run is done. The short delay keeps
  // the completion message on screen long enough to read.
  useEffect(() => {
    if (!isDemoComplete) return;
    const timer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('demo:finished'));
    }, 4000);
    return () => clearTimeout(timer);
  }, [isDemoComplete]);

  const handleStartDemo = () => {
    resetDemo();
    window.dispatchEvent(new CustomEvent('demo:started'));
    startDemo();
  };

  const handleResetDemo = () => {
    resetDemo();
    setActiveModel(null);
  };

  return (
    <div className="demo-runner">
      {/* Demo Control Panel */}
      <div className="demo-controls">
        <div className="demo-info">
          <h3>Interactive Demo</h3>
          <p>Experience the full ML pipeline with a pre-configured dataset.</p>
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Quick start</summary>
            <ol style={{ margin: '8px 0 0 18px', fontSize: 13 }}>
              <li>Click Run Demo to auto-train and set an active model.</li>
              <li>Open Model Registry and validate model health/thresholds.</li>
              <li>Review interpretation confidence, then export artifacts.</li>
            </ol>
          </details>
          
          {demoConfig && (
            <div className="demo-config">
              <h4>Demo Configuration:</h4>
              <ul>
                <li><strong>Dataset:</strong> {demoConfig.dataset}</li>
                <li><strong>Task:</strong> {demoConfig.taskType}</li>
                <li><strong>Model:</strong> {demoConfig.modelType}</li>
                <li><strong>Target:</strong> {demoConfig.targetColumn}</li>
              </ul>
            </div>
          )}

          {demoError && (
            <div className="demo-error">
              <h4>Demo Error:</h4>
              <p>{demoError}</p>
            </div>
          )}

          {isDemoComplete && (
            <div className="demo-success">
              <h4>Demo Complete!</h4>
              <p>You've successfully completed the interactive demo. Try exploring the different tabs to see the results.</p>
            </div>
          )}
        </div>

        <div className="demo-actions">
          {!isDemoRunning ? (
            <button 
              className="demo-button primary-button"
              onClick={handleStartDemo}
            >
              Run Demo
            </button>
          ) : (
            <div className="demo-running">
              <div className="demo-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${(demoStep / 3) * 100}%` }}
                  />
                </div>
                <span>Step {demoStep} of 3</span>
              </div>
              
              <div className="demo-status">
                {demoStep === 1 && "Loading demo data..."}
                {demoStep === 2 && "Training model..."}
                {demoStep === 3 && "Demo complete!"}
              </div>
            </div>
          )}

          {(isDemoRunning || isDemoComplete) && (
            <button 
              className="secondary-button"
              onClick={handleResetDemo}
            >
              Reset Demo
            </button>
          )}
        </div>
      </div>

    </div>
  );
}

function DemoRunner() {
  return <DemoRunnerContent />;
}

export default DemoRunner;
